import { AfterContentInit, ChangeDetectorRef, Component, ContentChild, Input, OnDestroy } from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { LabelDirective } from '../label/label.directive';
import { AbstractControl, FormControl, FormControlName } from '@angular/forms';
import { ErrorComponent } from '../error/error.component';
import { Subscription } from 'rxjs';
import { InputType } from '@shared/components/input/root/input-type';

/** Internal ID **/
let ID: number = 0;

/**
 * Component that links a form input control with its label and manages the required state, shows errors and manages relationships.
 * It sets unique IDs for accessibility, tracks form control state changes,
 * and updates the required indicator based on validators.
 *
 * ```html
 * <app-input-group>
 *   <label appLabel>Example</label>
 *   <input type="text" appInput formControlName="example">
 * </app-input-group>
 * ```
 */
@Component({
  selector: 'app-input-group',
  imports: [
    NgTemplateOutlet,
    ErrorComponent,
    NgClass,
  ],
  templateUrl: './input-group.component.html',
  styleUrl: './input-group.component.css'
})
export class InputGroupComponent implements AfterContentInit, OnDestroy {
  @Input() public hideDisabled?: boolean = false;

  /**
   * The generic input component projected into this group.
   */
  @ContentChild(InputType, { static: false }) public genericInput?: InputType;

  /**
   * The label directive projected into this group.
   */
  @ContentChild(LabelDirective, { static: false }) public label?: LabelDirective;

  /**
   * Unique internal ID used for linking input and label elements.
   */
  public internalId: string = `ec-input-${ID++}`;

  /**
   * The actual input element instance used in this group.
   */
  public inputElement?: InputType;

  /**
   * The form control associated with the input element.
   */
  public formControl?: AbstractControl;

  /**
   * Indicates whether the input is required based on its validators.
   */
  public required?: boolean;

  /**
   * Subscription to form control status and value changes.
   */
  private statusSubscription?: Subscription;

  /** Disabled Carrier **/
  public isDisabled?: boolean;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  public ngAfterContentInit(): void {
    this.inputElement = this.genericInput;

    if (this.inputElement) {
      this.inputElement.setId(this.internalId);
      this.formControl = this.inputElement.getFormControl();

      if (this.formControl instanceof FormControlName) {
        this.formControl = this.formControl.control as AbstractControl;
      }

      this.setupDisabledListener(); // listen for disabled/enabled changes
      this.updateDisabledState();
      this.updateRequiredState();

      if (this.formControl) {
        this.statusSubscription = new Subscription();

        const statusSub = this.formControl.statusChanges?.subscribe(() => {
          Promise.resolve().then(() => {
            this.updateRequiredState();
            // updateDisabledState not guaranteed to fire on emitEvent:false, but keep as fallback
            this.updateDisabledState();
          });
        });

        const valueSub = this.formControl.valueChanges?.subscribe(() => {
          Promise.resolve().then(() => this.updateRequiredState());
        });

        this.statusSubscription.add(statusSub);
        this.statusSubscription.add(valueSub);
      }
    }

    if (this.label) {
      if (this.inputElement && typeof this.inputElement.setInternalLabel === 'function') {
        this.inputElement.setInternalLabel(this.label.getLabel());
      }
      this.label.setFor(this.internalId);
    }
  }

  /**
   * Reliably listen to disabled/enabled changes even when emitEvent:false is used.
   * Uses AbstractControl.registerOnDisabledChange which is invoked by Angular.
   */
  private setupDisabledListener(): void {
    if (!this.formControl) return;

    // Initialize once
    this.isDisabled = this.formControl.disabled;

    // registerOnDisabledChange exists on FormControl/AbstractControl
    const ctrl = this.formControl as any;
    if (typeof ctrl.registerOnDisabledChange === 'function') {
      ctrl.registerOnDisabledChange((isDisabled: boolean) => {
        this.isDisabled = isDisabled;
        this.cdr.markForCheck();
      });
    }
  }

  /**
   * Updates the required state by checking the form control's validators.
   */
  private updateRequiredState(): void {
    const control = this.formControl;

    if (control?.validator) {
      const temp = new FormControl();
      const result = control.validator(temp);
      this.required = !!result?.['required'];
    } else {
      this.required = false;
    }
  }

  /**
   * Fallback: read current disabled flag (won’t cover emitEvent:false changes alone).
   */
  private updateDisabledState(): void {
    if (this.formControl) {
      this.isDisabled = this.formControl.disabled;
    }
  }

  public ngOnDestroy(): void {
    this.statusSubscription?.unsubscribe();
  }
}
